import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import type { AngebotStatus } from '../lib/api-enums';
import {
  ANGEBOT_STATUS_LABELS as STATUS_LABELS,
  ANGEBOT_STATUS_BADGE as STATUS_COLORS,
} from '../lib/api-labels';
import { formatGermanDateFromTimestamp } from '../lib/date-format';

interface Angebot {
  id: string;
  angebotsnummer: string | null;
  kundeId: string;
  objektId: string | null;
  titel: string;
  status: AngebotStatus;
  gueltigBis: string | null;
  zahlungszielTage: number;
  notizen: string | null;
  summeNetto: string;
  summeUst: string;
  summeBrutto: string;
  versendetAm: string | null;
  angenommenAm: string | null;
  abgelehntAm: string | null;
  storniertAm: string | null;
  createdAt: string;
}

interface AngebotPositionRow {
  id: string;
  positionNr: number;
  bezeichnung: string;
  beschreibung: string | null;
  menge: string;
  einheit: string;
  einzelpreisNetto: string;
  ustSatz: string;
  rabattProzent: string;
  positionNetto: string | null;
  positionUst: string | null;
  positionBrutto: string | null;
}

interface Kunde {
  id: string;
  kundennummer: string;
  firmenname: string;
}

export const Route = createFileRoute('/angebote/$id')({
  beforeLoad: () => requireAccess('/api/v1/angebote'),
  component: AngebotDetailPage,
});

function AngebotDetailPage() {
  const { id } = Route.useParams();
  const router = useRouter();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['angebot', id],
    queryFn: async () => {
      try {
        return await api<{ item: Angebot; positionen: AngebotPositionRow[] }>(
          `/api/v1/angebote/${id}`,
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
  const kundeQ = useQuery({
    queryKey: ['kunde', q.data?.item.kundeId],
    queryFn: () => api<{ item: Kunde }>(`/api/v1/kunden/${q.data!.item.kundeId}`),
    enabled: !!q.data?.item.kundeId,
  });

  const [actionError, setActionError] = useState<unknown>(null);
  const action = useMutation({
    mutationFn: async (verb: 'senden' | 'annehmen' | 'ablehnen' | 'stornieren') => {
      return api(`/api/v1/angebote/${id}/${verb}`, { method: 'POST' });
    },
    onSuccess: () => {
      setActionError(null);
      void qc.invalidateQueries({ queryKey: ['angebot', id] });
      void qc.invalidateQueries({ queryKey: ['angebote'] });
    },
    onError: (err) => setActionError(err),
  });
  const del = useMutation({
    mutationFn: () => api(`/api/v1/angebote/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['angebote'] });
      void router.navigate({ to: '/angebote' });
    },
    onError: (err) => setActionError(err),
  });

  if (q.isLoading) return <div className="text-slate-500">Lade…</div>;
  if (q.error) return <ErrorBanner error={q.error} />;
  if (!q.data) {
    return (
      <div className="space-y-4">
        <Link to="/angebote" className="text-sm text-brand-700 hover:underline">
          ← zurück
        </Link>
        <div className="rounded-md border border-slate-200 bg-white p-6 text-slate-500">
          Angebot nicht gefunden.
        </div>
      </div>
    );
  }

  const angebot = q.data.item;
  const positionen = q.data.positionen;
  const kunde = kundeQ.data?.item ?? null;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/angebote" className="text-sm text-brand-700 hover:underline">
          ← zurück zur Angebotsliste
        </Link>
      </div>

      <PageHeader
        title={angebot.titel}
        description={
          angebot.angebotsnummer
            ? `${angebot.angebotsnummer} · ${STATUS_LABELS[angebot.status]}`
            : `Entwurf · ${STATUS_LABELS[angebot.status]}`
        }
        actions={
          <div className="flex items-center gap-2">
            {angebot.status === 'entwurf' && (
              <>
                <Button
                  onClick={() => action.mutate('senden')}
                  disabled={action.isPending || positionen.length === 0}
                >
                  Versenden
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (confirm('Entwurf wirklich löschen?')) del.mutate();
                  }}
                  disabled={action.isPending || del.isPending}
                >
                  Löschen
                </Button>
              </>
            )}
            {angebot.status === 'versendet' && (
              <>
                <Button onClick={() => action.mutate('annehmen')} disabled={action.isPending}>
                  Annehmen
                </Button>
                <Button
                  variant="danger"
                  onClick={() => action.mutate('ablehnen')}
                  disabled={action.isPending}
                >
                  Ablehnen
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => action.mutate('stornieren')}
                  disabled={action.isPending}
                >
                  Stornieren
                </Button>
              </>
            )}
          </div>
        }
      />

      <ErrorBanner error={actionError} />

      <Panel title="Stammdaten">
        <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
          <Field label="Status">
            <span
              className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[angebot.status]}`}
            >
              {STATUS_LABELS[angebot.status]}
            </span>
          </Field>
          <Field label="Nummer">
            <span className="font-mono text-slate-600">
              {angebot.angebotsnummer ?? <span className="italic text-slate-400">noch keine</span>}
            </span>
          </Field>
          <Field label="Kunde">
            {kunde ? (
              <Link
                to="/kunden/$id"
                params={{ id: kunde.id }}
                className="text-brand-700 hover:underline"
              >
                {kunde.kundennummer} — {kunde.firmenname}
              </Link>
            ) : (
              <span className="text-slate-400">Lade…</span>
            )}
          </Field>
          <Field label="Gültig bis">
            <span className="text-slate-600">{angebot.gueltigBis ?? '–'}</span>
          </Field>
          <Field label="Zahlungsziel">
            <span className="text-slate-600">{angebot.zahlungszielTage} Tage</span>
          </Field>
          <Field label="Angelegt">
            <span className="text-slate-500">
              {formatGermanDateFromTimestamp(angebot.createdAt)}
            </span>
          </Field>
          <Field label="Versendet">
            <span className="text-slate-500">
              {angebot.versendetAm ? formatGermanDateFromTimestamp(angebot.versendetAm) : '–'}
            </span>
          </Field>
          <Field label="Angenommen">
            <span className="text-slate-500">
              {angebot.angenommenAm ? formatGermanDateFromTimestamp(angebot.angenommenAm) : '–'}
            </span>
          </Field>
          <Field label="Abgelehnt">
            <span className="text-slate-500">
              {angebot.abgelehntAm ? formatGermanDateFromTimestamp(angebot.abgelehntAm) : '–'}
            </span>
          </Field>
          {angebot.notizen && (
            <div className="md:col-span-3">
              <Field label="Notizen">
                <span className="text-slate-600 whitespace-pre-wrap">{angebot.notizen}</span>
              </Field>
            </div>
          )}
        </dl>
      </Panel>

      <Panel title={`Positionen (${positionen.length})`}>
        {positionen.length === 0 ? (
          <div className="text-slate-400 text-sm">Keine Positionen.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr className="border-b border-slate-100">
                <th className="py-1.5 text-left w-8">#</th>
                <th className="py-1.5 text-left">Bezeichnung</th>
                <th className="py-1.5 text-right w-20">Menge</th>
                <th className="py-1.5 text-left w-16">Einheit</th>
                <th className="py-1.5 text-right w-24">Einzelpreis</th>
                <th className="py-1.5 text-right w-16">USt %</th>
                <th className="py-1.5 text-right w-16">Rabatt</th>
                <th className="py-1.5 text-right w-28">Netto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {positionen.map((p) => (
                <tr key={p.id} className="text-slate-800">
                  <td className="py-1.5 text-xs text-slate-400">{p.positionNr}</td>
                  <td className="py-1.5">
                    <div className="font-medium">{p.bezeichnung}</div>
                    {p.beschreibung && (
                      <div className="text-xs text-slate-500">{p.beschreibung}</div>
                    )}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{formatDe(p.menge, 3)}</td>
                  <td className="py-1.5 text-slate-600">{p.einheit}</td>
                  <td className="py-1.5 text-right tabular-nums">
                    {formatDe(p.einzelpreisNetto, 2)} €
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-slate-600">{p.ustSatz}</td>
                  <td className="py-1.5 text-right tabular-nums text-slate-600">
                    {Number(p.rabattProzent) > 0 ? `${p.rabattProzent}%` : '–'}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {p.positionNetto ? formatDe(p.positionNetto, 2) : '–'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200 text-sm font-medium">
                <td colSpan={7} className="py-1.5 text-right text-slate-600">
                  Summe netto
                </td>
                <td className="py-1.5 text-right tabular-nums">
                  {formatDe(angebot.summeNetto, 2)} €
                </td>
              </tr>
              <tr className="text-sm text-slate-600">
                <td colSpan={7} className="py-1 text-right">
                  USt
                </td>
                <td className="py-1 text-right tabular-nums">{formatDe(angebot.summeUst, 2)} €</td>
              </tr>
              <tr className="text-sm font-semibold">
                <td colSpan={7} className="py-1 text-right">
                  Brutto
                </td>
                <td className="py-1 text-right tabular-nums">
                  {formatDe(angebot.summeBrutto, 2)} €
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </Panel>
    </div>
  );
}

function Panel({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {actions}
      </header>
      <div className="p-4 space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

function formatDe(value: string, digits: number): string {
  return Number(value).toLocaleString('de-DE', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}
